/* Height-animated <details> rows.
   Progressive enhancement: without this script the rows still open and close
   natively, just instantly. Everything here only intercepts the toggle to
   interpolate the height in between — under prefers-reduced-motion it steps
   aside entirely and lets the browser do its native, instant thing. */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const DURATION = 260;
// read the theme's curve so JS and CSS can't drift apart; the literal is the
// same value, kept as a fallback in case base.css hasn't landed yet
const EASING =
  getComputedStyle(document.documentElement).getPropertyValue('--ease-out').trim() ||
  'cubic-bezier(0.23, 1, 0.32, 1)';

class AnimatedDetails {
  constructor(details) {
    this.details = details;
    this.summary = details.querySelector('summary');
    this.content = details.querySelector('[data-details-content]');
    // rows with no answer have nothing to reveal — leave them native
    if (!this.summary || !this.content) return;

    this.animation = null;
    this.isOpening = false;
    this.isClosing = false;
    this.summary.addEventListener('click', (event) => this.onClick(event));
  }

  onClick(event) {
    if (window.matchMedia(REDUCED_MOTION).matches) return;
    event.preventDefault();
    this.details.style.overflow = 'hidden';
    // read the intent, not the attribute: mid-animation the attribute is
    // already at its destination while the row is still travelling
    if (this.isClosing || !this.details.open) this.expand();
    else this.collapse();
  }

  /* the row's height with the answer hidden — measured rather than derived,
     because the open state also changes the question's bottom padding */
  closedHeight() {
    const wasOpen = this.details.open;
    this.details.open = false;
    const height = this.details.offsetHeight;
    this.details.open = wasOpen;
    return height;
  }

  expand() {
    this.isOpening = true;
    this.isClosing = false;
    this.details.classList.remove('is-closing');
    const start = this.details.offsetHeight;
    this.cancel();
    this.details.open = true;
    this.run(start, this.details.offsetHeight);
    this.content.animate({ opacity: [0, 1] }, { duration: DURATION * 0.75, easing: EASING });
  }

  collapse() {
    this.isClosing = true;
    this.isOpening = false;
    // [open] has to stay set while the row collapses, so anything that should
    // follow the gesture rather than the attribute keys off this instead
    this.details.classList.add('is-closing');
    const start = this.details.offsetHeight;
    this.cancel();
    const end = this.closedHeight();
    // stays open while it travels; the attribute drops once it lands
    this.run(start, end, () => {
      this.details.open = false;
    });
  }

  cancel() {
    if (!this.animation) return;
    this.animation.cancel();
    this.animation = null;
  }

  run(start, end, onFinish) {
    const animation = this.details.animate(
      { height: [`${start}px`, `${end}px`] },
      { duration: DURATION, easing: EASING }
    );
    this.animation = animation;
    // both events fire asynchronously — by the time they run this row may
    // already be travelling the other way, so only the current animation is
    // allowed to clear the reference or land the final state
    animation.onfinish = () => {
      if (this.animation !== animation) return;
      this.animation = null;
      this.isOpening = false;
      this.isClosing = false;
      if (onFinish) onFinish();
      this.details.classList.remove('is-closing');
      this.details.style.overflow = '';
    };
    animation.oncancel = () => {
      if (this.animation === animation) this.animation = null;
    };
  }
}

class RisifyDetailsList extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.querySelectorAll('details').forEach((details) => new AnimatedDetails(details));
  }
}

customElements.define('risify-details-list', RisifyDetailsList);
